import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { aboutInfo } from '@/mocks/data';

export default function AboutPage() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = fd.get('company_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setSubmitStatus('success');
      setFormData({ name: '', email: '', message: '' });
      return;
    }
    try {
      const res = await fetch('https://readdy.ai/api/form/d95mf4bjjt5fme3c4vv0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: fd.get('name') as string,
          email: fd.get('email') as string,
          message: fd.get('message') as string,
        }).toString(),
      });
      const text = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(text); } catch { /* ignore parse errors */ }
      if (res.ok && parsed?.code === 'OK') {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
        setErrorMsg(parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试');
      }
    } catch (_err) {
      setSubmitStatus('error');
      setErrorMsg('网络错误，请检查网络后重试');
    }
  };

  // JSON-LD Structured Data
  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: aboutInfo.brandName,
    url: 'https://51chigua.com',
    description: aboutInfo.brandIntro,
    contactPoint: {
      '@type': 'ContactPoint',
      email: aboutInfo.contactEmail,
      contactType: 'customer service',
    },
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutInfo.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://51chigua.com' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />

      <Navbar />

      <main>
        {/* Page Header */}
        <section className="relative w-full h-[200px] md:h-[260px] overflow-hidden bg-background-950">
          <div className="absolute inset-0 opacity-20">
            <img
              src="https://readdy.ai/api/search-image?query=Dark%20elegant%20abstract%20journalism%20atmosphere%20with%20warm%20amber%20and%20gold%20subtle%20light%20streaks%2C%20professional%20media%20organization%20background%2C%20cinematic%20depth%20with%20soft%20bokeh%20and%20deep%20shadows%2C%20premium%20newsroom%20aesthetic%20with%20sophisticated%20warm%20tones%2C%20mysterious%20editorial%20composition%2C%20luxury%20media%20brand%20mood&width=1600&height=600&seq=about-header-bg&orientation=landscape"
              alt="关于51吃瓜爆料网"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="relative z-10 w-full h-full flex flex-col justify-center px-4 md:px-6">
            <div className="max-w-3xl">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-background-400 text-xs mb-4 flex-wrap" aria-label="面包屑导航">
                <a href="/" className="hover:text-background-200 transition-colors">首页</a>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-background-200">关于我们</span>
              </nav>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50">
                关于我们
              </h1>
            </div>
          </div>
        </section>

        {/* Brand Story Section */}
        <section className="py-16 md:py-22 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
              {/* Left: Tag */}
              <div className="lg:w-1/3">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-medium mb-4">
                  关于 51吃瓜爆料网
                </span>
              </div>

              {/* Right: Content */}
              <div className="lg:w-2/3">
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-6 leading-tight">
                  {aboutInfo.mission}
                </h2>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-8">
                  {aboutInfo.brandIntro}
                </p>

                {/* Values */}
                <div className="flex flex-wrap gap-3 mb-10">
                  {aboutInfo.values.map((v) => (
                    <span key={v} className="px-4 py-2 rounded-full bg-secondary-100 text-secondary-900 text-sm font-medium">
                      {v}
                    </span>
                  ))}
                </div>

                {/* Images */}
                <div className="flex gap-4 h-[280px] md:h-[350px]">
                  <div className="w-2/5 rounded-2xl overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20workspace%20with%20warm%20amber%20and%20gold%20ambient%20lighting%2C%20editorial%20newsroom%20aesthetic%20with%20sophisticated%20atmosphere%2C%20elegant%20dark%20wood%20desks%20and%20warm%20toned%20walls%2C%20premium%20media%20company%20interior%20design%2C%20soft%20natural%20light%20through%20large%20windows%2C%20professional%20yet%20cozy%20journalism%20environment%2C%20abstract%20luxury%20workspace%20mood&width=600&height=800&seq=about-team-office&orientation=portrait"
                      alt="51吃瓜爆料网 团队"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="w-3/5 rounded-2xl overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Dynamic%20modern%20newsroom%20with%20multiple%20screens%20displaying%20breaking%20entertainment%20news%2C%20warm%20amber%20and%20deep%20gold%20ambient%20lighting%2C%20professional%20journalism%20atmosphere%20with%20sophisticated%20aesthetic%2C%20team%20collaboration%20around%20digital%20displays%2C%20premium%20media%20production%20environment%2C%20editorial%20energy%20with%20cinematic%20composition%2C%20abstract%20newsroom%20mood&width=1000&height=700&seq=about-newsroom&orientation=landscape"
                      alt="51吃瓜爆料网 工作场景"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Reputation Section */}
        <section className="py-14 md:py-18 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-foreground-400 font-label text-xs tracking-widest uppercase block mb-3">
                行业口碑
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                他们眼中的我们
              </h2>
            </div>

            <div className="space-y-6">
              {aboutInfo.reputation.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-medium mb-4">
                    <i className="ri-double-quotes-l text-accent-500"></i>
                    {item.media}
                  </div>
                  <blockquote className="font-heading text-foreground-800 text-base md:text-lg leading-relaxed italic mb-4">
                    &ldquo;{item.quote}&rdquo;
                  </blockquote>
                  <p className="text-foreground-500 text-sm">
                    — {item.author}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-foreground-400 font-label text-xs tracking-widest uppercase block mb-3">
                联系我们
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                有料想爆？有瓜要扒？
              </h2>
              <p className="text-foreground-500 text-sm">
                欢迎随时与我们取得联系，你的每一条线索都可能是下一个大瓜
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-10">
              {/* Contact Info */}
              <div className="lg:w-2/5 space-y-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-foreground-900 font-medium text-sm mb-1">邮箱</p>
                    <p className="text-foreground-500 text-sm">{aboutInfo.contactEmail}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-wechat-line text-accent-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-foreground-900 font-medium text-sm mb-1">微信公众号</p>
                    <p className="text-foreground-500 text-sm">{aboutInfo.contactWechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-secondary-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-foreground-900 font-medium text-sm mb-1">工作时间</p>
                    <p className="text-foreground-500 text-sm">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="lg:w-3/5">
                <form onSubmit={handleSubmit} className="bg-background-100 rounded-2xl p-6 md:p-8" data-readdy-form="">
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-foreground-700 text-sm font-medium mb-1.5">你的名字</label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                        required
                        className="w-full bg-background-50 border border-background-200/70 rounded-xl px-4 py-3 text-sm text-foreground-800 outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入你的名字"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-foreground-700 text-sm font-medium mb-1.5">邮箱地址</label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                        required
                        className="w-full bg-background-50 border border-background-200/70 rounded-xl px-4 py-3 text-sm text-foreground-800 outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入你的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-foreground-700 text-sm font-medium mb-1.5">爆料内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        value={formData.message}
                        onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value.slice(0, 500) }))}
                        required
                        rows={4}
                        className="w-full bg-background-50 border border-background-200/70 rounded-xl px-4 py-3 text-sm text-foreground-800 outline-none focus:border-primary-300 transition-colors resize-none"
                        placeholder="请描述你的爆料内容..."
                      />
                      <p className="text-foreground-400 text-xs mt-1">{formData.message.length}/500</p>
                    </div>
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="website_alt_field"
                    />
                    <button
                      type="submit"
                      className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      提交爆料
                    </button>
                  </div>
                  {submitStatus === 'success' && (
                    <p className="text-accent-600 text-sm mt-3 text-center">提交成功！我们会尽快查证处理，感谢你的爆料！</p>
                  )}
                  {submitStatus === 'error' && (
                    <p className="text-primary-500 text-sm mt-3 text-center">{errorMsg}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-foreground-400 font-label text-xs tracking-widest uppercase block mb-3">
                常见问题
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                你可能想知道的
              </h2>
            </div>

            <div className="space-y-3">
              {aboutInfo.faqs.map((faq, idx) => (
                <details key={idx} className="group bg-background-50 rounded-2xl border border-background-200/70">
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                    <span className="font-heading text-foreground-900 text-sm md:text-base font-medium pr-4">{faq.question}</span>
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-background-200/70 flex items-center justify-center group-open:bg-primary-500 group-open:text-background-50 transition-all duration-200">
                      <i className="ri-add-line text-sm group-open:rotate-45 transition-transform duration-200"></i>
                    </span>
                  </summary>
                  <div className="px-6 pb-5">
                    <p className="text-foreground-600 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}